// components/ProtectedRoute.tsx
import React from 'react';
import { Navigate } from 'react-router-dom';
import { loginPathForBuild } from '../utils/postLogout';
import { useAuth } from '../context/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: string;
  /**
   * Mode « au moins un droit parmi la liste » (OU logique). Si l'utilisateur
   * détient AU MOINS UN des droits listés, l'accès est autorisé. Utilisé pour
   * les écrans terrain partagés entre releveurs (`acces_releve_terrain`) et
   * agents BI (`acces_intervention_terrain`). Indépendant de `requiredPermission`
   * (qui reste un ET implicite si les deux sont fournis).
   */
  requiredAnyPermission?: string[];
  requiredRole?: string;
  requiredModule?: string;
  /**
   * Si défini, en cas de permission/module manquant on redirige vers ce
   * chemin AU LIEU de `/unauthorized` (qui bloque). Utilisé pour
   * `/dashboard` → `/mon-espace` : on ne « bloque » jamais un agent qui n'a
   * pas le droit `access_dashboard`, on le ramène simplement chez lui.
   */
  fallbackPath?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermission,
  requiredAnyPermission,
  requiredRole,
  requiredModule,
  fallbackPath,
}) => {
  const { isAuthenticated, user, hasPermission, hasModule, permissionsLoaded, loading } = useAuth();

  // Afficher un loader pendant le chargement
  if (loading || !permissionsLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Vérification des accès...</p>
        </div>
      </div>
    );
  }

  // Vérifier l'authentification
  if (!isAuthenticated) {
    // Les routes /terrain/* redirigent vers le login mobile dédié
    const isTerrainRoute =
      typeof window !== 'undefined' &&
      window.location.pathname.includes('/terrain');
    const loginTarget = isTerrainRoute ? '/terrain/login' : loginPathForBuild();
    return <Navigate to={loginTarget} replace />;
  }

  // Vérifier le rôle si spécifié
  if (requiredRole && user?.role !== requiredRole) {
    return (
      <Navigate
        to="/unauthorized"
        replace
        state={{
          requiredRole,
          attemptedPath: typeof window !== 'undefined' ? window.location.pathname : null,
          reason: 'role',
        }}
      />
    );
  }

  // Vérifier la permission si spécifiée
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <Navigate
        to={fallbackPath || '/unauthorized'}
        replace
        state={{
          requiredPermission,
          attemptedPath: typeof window !== 'undefined' ? window.location.pathname : null,
          reason: 'permission',
        }}
      />
    );
  }

  // Vérifier le mode « au moins un droit parmi » (OU logique) si spécifié
  if (
    requiredAnyPermission &&
    requiredAnyPermission.length > 0 &&
    !requiredAnyPermission.some((p) => hasPermission(p))
  ) {
    return (
      <Navigate
        to={fallbackPath || '/unauthorized'}
        replace
        state={{
          requiredPermission: requiredAnyPermission.join(' | '),
          attemptedPath: typeof window !== 'undefined' ? window.location.pathname : null,
          reason: 'permission',
        }}
      />
    );
  }

  // Vérifier le module si spécifié
  if (requiredModule && !hasModule(requiredModule)) {
    return (
      <Navigate
        to={fallbackPath || '/unauthorized'}
        replace
        state={{
          requiredModule,
          attemptedPath: typeof window !== 'undefined' ? window.location.pathname : null,
          reason: 'module',
        }}
      />
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;